import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, catchError, EMPTY, interval, map, startWith, Subject, Subscription, switchMap, take, takeUntil } from "rxjs";
import { ExpandableAccordionComponent } from "../../../components/common/expandable-accordion/expandable-accordion.component";
import {
  TcgGenerateFormData,
  TcgGenerateModalComponent,
} from "../../../components/modal-templates/tcg-generate-modal/tcg-generate-modal.component";
import {
  PromptFilterModalComponent,
  TcgFilterResult,
  TcgFilterState,
} from "../../../components/modal-templates/prompt-filter-modal/prompt-filter-modal.component";
import { TcgStoryDetailModalComponent } from "../../../components/modal-templates/tcg-story-detail-modal/tcg-story-detail-modal.component";
import { TcgTraceabilityModalComponent } from "../../../components/modal-templates/tcg-traceability-modal/tcg-traceability-modal.component";
import { TestCoverageConfigs } from "../../../components/modal-templates/test-coverage/test-coverage.component";
import { userstoryDetails } from "../../../core/dummy/dummy-data";
import { TcgTestCaseDetailComponent } from "../../../components/tcg/tcg-test-case-detail/tcg-test-case-detail.component";
import { TcgTestSuiteRow, TcgTestSuitesComponent } from "../../../components/tcg/tcg-test-suites/tcg-test-suites.component";
import { TcgUserStoriesComponent, UserStory } from "../../../components/tcg/tcg-user-stories/tcg-user-stories.component";
import { IBreadcrumb } from "../../../core/models/common";
import {
  RequirementDetailsData,
  RequirementDetailsModalComponent,
} from "../../../components/modal-templates/requirement-details-modal/requirement-details-modal.component";
import {
  ImportTestCasesModalComponent,
  ImportTestCasesResult,
  OpenZephyrImportResult,
} from "../../../components/modal-templates/import-test-cases-modal/import-test-cases-modal.component";
import {
  ImportZephyrModalComponent,
  ZephyrModalResult,
} from "../../../components/modal-templates/import-zephyr-modal/import-zephyr-modal.component";
import {
  ImportAddToSuiteModalComponent,
} from "../../../components/modal-templates/import-add-to-suite-modal/import-add-to-suite-modal.component";
import {
  ImportPreviewModalComponent,
  ImportPreviewResult,
} from "../../../components/modal-templates/import-preview-modal/import-preview-modal.component";
import { MessageEnum } from "../../../core/utils/message.enum";
import { CommonService } from "../../../core/services/common.service";
import { BaseLayoutComponent } from "../../base-layout.component";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import { selectAllTestCaseVersions } from "../../../core/ngrx/selectors/testcase-generator.selectors";

/** Which sub-view of the TCG feature is currently shown. */
type TcgView = "testSuites" | "testCaseDetail" | "userStories" | "testCases";

/**
 * TCG (Test Case Generator) container layout.
 *
 * The shared breadcrumb header (side-nav) hosts the "+ Generate Test Case"
 * CTA via page actions so it sits on the same row as "Design | Test Case Generator"
 * (Figma: node-id=10860-12862). Sub-views swap below via `*ngIf`:
 * - `testSuites`: Test Suites list
 * - `testCaseDetail`: suite drill-down
 * - `generate`: Generate Test Case form
 */
@Component({
  selector: "app-tcg",
  standalone: true,
  imports: [CommonModule, TcgTestSuitesComponent, TcgTestCaseDetailComponent, TcgUserStoriesComponent, ExpandableAccordionComponent],
  templateUrl: "./tcg.component.html",
  styleUrl: "./tcg.component.scss",
})
export class TcgComponent extends BaseLayoutComponent implements OnInit, OnDestroy {
  activeView: TcgView = "testSuites";
  selectedSuite: TcgTestSuiteRow | null = null;
  extractedStories: UserStory[] = [];
  extractConfigId = '';
  /** ALM Extract From date from the generate modal — sent on progress polls. */
  extractFromTimestamp: string | null = null;
  processingCounts: any = null;
  /** True while progress API is polled; drives us-counts-bar visibility. */
  isExtracting = false;
  /** Server-side pagination state for user stories. */
  storyPageIndex = 0;
  storyPageSize  = 10;
  storyTotalElements = 0;
  storyTotalPages    = 0;
  /**
   * Tracks the page the user has explicitly navigated to.
   * Polling always fetches page 0; this lets handleExtractionProgress
   * skip overwriting stories/pageIndex when the user is on a different page.
   */
  private storyViewingPageIndex = 0;
  generatedTestCases: any = null;
  /** Full unfiltered rows for the generated test-cases view. */
  private allGeneratedTestCaseRows: any[] = [];
  /** Active filter for generated test cases (same modal as user stories). */
  previousTestCaseFilter: TcgFilterState | null = null;
  private extractPollSub: Subscription | null = null;
  /** Version ID returned by generateTestCase — required for persist. */
  testCaseVersionId = '';
  /** True while Update from Feedback API is in flight. */
  isUpdatingFeedback = false;

  get isTestCaseFilterActive(): boolean {
    return !!(this.previousTestCaseFilter?.userStory || this.previousTestCaseFilter?.priority);
  }
  isPersistingSuite = false;
  /** True only when testCaseDetail is opened from the BPD ("From other documents") pipeline. */
  isBpdDetailView = false;
  /** True only when the detail view was reached via the Import Test Cases flow. */
  isImportDetailView = false;
  /** connectionId of the imported file — needed for the "Add to Suite" API call. */
  private importConnectionId = '';
  /** 'xlsx' | 'csv' | 'zephyr' — determines the origin value (EXCEL / CSV / ZEPHYR_ESSENTIAL). */
  private importDocumentType = 'xlsx';

  /** Completion percentage (0–100) derived from live processingCounts. */
  get extractionPct(): number {
    const c = this.processingCounts;
    if (!c?.identified) return 0;
    return Math.min(100, Math.round((c.extractionCompleted / c.identified) * 100));
  }

  /**
   * Top progress line + counts bar: visible while extracting until
   * Identified === Extraction Completed. Polling continues until COMPLETED.
   */
  get showExtractionProgressUi(): boolean {
    if (!this.isExtracting) return false;
    const c = this.processingCounts;
    if (!c) return true;
    const identified = Number(c.identified) || 0;
    const completed = Number(c.extractionCompleted) || 0;
    return !(identified > 0 && identified === completed);
  }

  private readonly activeFilter$ = new BehaviorSubject<TcgFilterState | null>(null);

  isFilterActive$ = this.activeFilter$.pipe(
    map((filter) => !!(filter?.userStory || filter?.priority))
  );

  /**
   * Same shape used by agentic AI (`workspace-agentic-ai`): real API params so
   * TestCoverageComponent runs startAnalysis → poll getResults (no dummy data).
   */
  get testCoverageConfigs(): TestCoverageConfigs {
    return {
      apiParams: {
        project_id: this.projectId,
        version_id: this.testCaseVersionId,
        config_id: this.extractConfigId,
      },
    };
  }

  private readonly destroy$ = new Subject<void>();
  private readonly modalService = inject(NgbModal);
  private readonly commonService = inject(CommonService);
  private static readonly GENERATE_ACTION_KEY = "generateTestCase";
  private static readonly IMPORT_ACTION_KEY = "importTestCases";

  // ── Dummy data wired to the Requirement Details modal demo button ────────
  private readonly dummyRequirementDetails: RequirementDetailsData = {
    nameInfo: {
      name: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      id: "Fn_Req_001_2026_12_01",
      path: "CompanyHierarchyTreeView > TenantHierarchyNode > BranchHierarchyNode > BrandManufacturerHierarchyNode",
    },
    requirements: [
      {
        requirementId: "Fn_Req_001_2026_12_01",
        requirementTitle:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
      {
        requirementId: "Fn_Req_001_2026_12_02",
        requirementTitle:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
      {
        requirementId: "Fn_Req_001_2026_12_03",
        requirementTitle:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
      {
        requirementId: "Fn_Req_001_2026_12_04",
        requirementTitle:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
    ],
    validation: [
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    ],
    uiBehavior: [
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    ],
    errorConditions: [
      {
        condition:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
        expectedResult:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
      {
        condition:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
        expectedResult:
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      },
    ],
    dependencies: [
      { section: "Lorem ipsum dolor sit", dependency: "Lorem ipsum dolor sit amet, consectetur" },
      { section: "Lorem ipsum dolor", dependency: "Lorem ipsum dolor sit amet, consectetur adipiscing elit." },
      {
        section: "Vestibulum ante",
        dependency: "Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.",
      },
      {
        section: "Curabitur feugiat",
        dependency: "Curabitur feugiat, nisl a convallis blandit, justo erat tempus nulla, a dignissim lacus orci a metus.",
      },
    ],
    headingPath: [
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt",
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    ],
  };

  ngOnInit(): void {
    this.syncHeaderActions();

    this.breadcrumbService
      .onNodeClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe((node: IBreadcrumb) => {
        if (node.key === "test_case_generator") {
          this.onBackToList();
        }
      });

    this.breadcrumbService
      .onPageAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe((key: string) => {
        if (key === TcgComponent.GENERATE_ACTION_KEY) {
          this.onGenerateTestCase();
        } else if (key === TcgComponent.IMPORT_ACTION_KEY) {
          this.onImportTestCases();
        }
      });
  }

  openRequirementDetails(): void {
    const ref = this.modalService.open(RequirementDetailsModalComponent, {
      centered: true,
      scrollable: false,
      windowClass: "rd-modal-window",
    });
    ref.componentInstance.data = this.dummyRequirementDetails;
  }

  ngOnDestroy(): void {
    this.stopExtractionPolling();
    this.breadcrumbService.clearPageActions();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get breadcrumbData(): IBreadcrumb[] {
    const nodes: IBreadcrumb[] = [
      { name: "Design", key: "design", isRootNode: true, clickable: false },
      {
        name: "Test Case Generator",
        key: "test_case_generator",
        clickable: this.activeView !== "testSuites",
      },
    ];

    if (this.activeView === "testCaseDetail" && this.selectedSuite) {
      nodes.push({ name: this.selectedSuite.testSuiteName, key: "suite", clickable: false });
    }

    if (this.activeView === "testCases") {
      nodes.push({ name: "Generate Test Case", key: "generate_tc", clickable: false });
    }

    return nodes;
  }

  get projectId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  onGenerateTestCase(): void {
    const ref = this.modalService.open(TcgGenerateModalComponent, {
      centered: true,
      windowClass: 'tcg-generate-modal-window',
      backdrop: 'static',
      keyboard: false,
    });
    ref.componentInstance.projectId = this.projectId;

    ref.result.then((result: TcgGenerateFormData) => {
      if (result?.source === 'other_documents') {
        this.handleBpdTestCasesResult(result);
        return;
      }

      // User-stories extraction: close modal without progress bar; poll here.
      this.extractConfigId = result?.extractConfigId ?? '';
      this.extractFromTimestamp = result?.fromTimestamp ?? null;
      this.extractedStories = this.mapStories(result?.extractedStories ?? []);
      this.processingCounts = result?.processingCounts ?? null;
      this.activeView = 'userStories';
      this.syncHeaderActions();

      if (result?.pollExtraction && this.extractConfigId) {
        this.startExtractionPolling();
      } else if (this.extractedStories.length) {
        this.isExtracting = false;
        this.processingCounts = null;
        this.toaster.pop({ type: 'success', text: 'User stories extracted successfully.' });
      }
    }).catch(() => {
      // dismissed – no action needed
    });
  }

  private mapStories(items: any[]): UserStory[] {
    return (items ?? []).map((item: any) => ({
      storyId:            item.id ?? item.testCaseId ?? item.storyId ?? item.key ?? '',
      summary:            item.title ?? item.testCaseTitle ?? item.summary ?? item.name ?? '',
      rciScore:           item.score ?? item.rciScore ?? item.rating ?? undefined,
      description:        item.description ?? item.details ?? undefined,
      acceptanceCriteria: item.acceptanceCriteria ?? item.criteria ?? undefined,
      qualityReportId:    item.qualityReportId ?? item.reportId ?? undefined,
      ...item,
    }));
  }

  /**
   * Poll GET .../progress every 5s (page 0). Updates counts bar and
   * replaces page-0 stories until completionPercentage reaches 100.
   */
  private startExtractionPolling(): void {
    this.stopExtractionPolling();
    this.isExtracting = true;
    this.storyPageIndex = 0;
    this.storyViewingPageIndex = 0;
    this.processingCounts = this.processingCounts ?? {
      identified: 0,
      newCount: 0,
      extractionCompleted: 0,
      extractionPending: 0,
      rciGenerated: 0,
      kgGenerated: 0,
    };

    this.extractPollSub = interval(5000).pipe(
      startWith(0),
      switchMap(() =>
        this.restService.getUserStoryProgress(
          this.projectId,
          this.extractConfigId,
          0,
          this.storyPageSize,
          this.extractFromTimestamp,
        ).pipe(
          catchError((err: any) => {
            console.error('[TCG] Progress polling failed:', err);
            this.toaster.pop({
              type: 'error',
              text: err?.error?.message || err?.message || 'Error tracking extraction progress.',
            });
            this.stopExtractionPolling();
            this.isExtracting = false;
            this.processingCounts = null;
            return EMPTY;
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe((res: any) => this.handleExtractionProgress(res));
  }

  /** Fetch a specific page of user stories without touching the polling cycle. */
  fetchStoryPage(page: number): void {
    // Track which page the user is currently viewing so that poll responses
    // (which always fetch page 0) don't overwrite stories on other pages.
    this.storyViewingPageIndex = page;
    this.restService.getUserStoryProgress(
      this.projectId,
      this.extractConfigId,
      page,
      this.storyPageSize,
      this.extractFromTimestamp,
    ).pipe(
      catchError((err: any) => {
        this.toaster.pop({
          type: 'error',
          text: err?.error?.message || err?.message || 'Error loading user stories.',
        });
        return EMPTY;
      })
    ).subscribe((res: any) => {
      const data = res?.data ?? {};
      this.storyPageIndex   = Number(data.number ?? page);
      this.storyTotalElements = Number(data.totalElements ?? this.storyTotalElements);
      this.storyTotalPages    = Number(data.totalPages    ?? this.storyTotalPages);
      const batch = data.content ?? [];
      this.extractedStories = Array.isArray(batch) ? this.mapStories(batch) : [];
    });
  }

  private handleExtractionProgress(res: any): void {
    const data = res?.data ?? {};
    const progress = data.progress ?? {};
    const pct = Number(data.completionPercentage ?? 0);
    const status = (progress.status ?? '').toString().toLowerCase();

    // Always update pagination totals (counts bar depends on totalElements).
    this.storyTotalElements = Number(data.totalElements ?? this.storyTotalElements);
    this.storyTotalPages    = Number(data.totalPages    ?? this.storyTotalPages);

    // Polling always fetches page 0. Only overwrite the displayed stories and
    // page index when the user hasn't navigated away from page 0; otherwise
    // keep whatever fetchStoryPage() loaded for the user's current page.
    if (this.storyViewingPageIndex === 0) {
      this.storyPageIndex = Number(data.number ?? 0);
      const batch =
        data.content ??
        progress.content ??
        data.userStories ??
        data.stories ??
        [];
      if (Array.isArray(batch)) {
        this.extractedStories = this.mapStories(batch);
      }
    }

    const listCount = Number(
      data.totalElements ??
      this.storyTotalElements ??
      0
    ) || 0;

    this.processingCounts = this.mapProgressToCounts(progress, listCount);

    const done = pct >= 100 || status === 'completed';
    if (done) {
      this.stopExtractionPolling();
      this.isExtracting = false;
      this.processingCounts = null;
      this.toaster.pop({ type: 'success', text: 'User stories extracted successfully.' });
      return;
    }

    if (status === 'failed') {
      const failMsg = progress.tcgKgMessage || data.message || 'User story extraction failed.';
      this.stopExtractionPolling();
      this.isExtracting = false;
      this.processingCounts = null;
      this.toaster.pop({ type: 'error', text: failMsg });
    }
  }

  private mapProgressToCounts(progress: any, listCount = 0): any {
    const identified = Number(
      progress.totalUserStoriesCount ?? progress.userStoriesCount ?? 0
    ) || 0;
    const rciGenerated = Number(
      progress.rciCompletedCount ?? progress.rciAnalyzedCount ?? 0
    ) || 0;
    const kgGenerated = Number(
      progress.kgCompletedCount ?? progress.kgGeneratedCount ?? 0
    ) || 0;
    // Completed = stories already in the list; Pending = identified − completed.
    const extractionCompleted = Math.min(Math.max(listCount, 0), identified || listCount);
    const extractionPending = Math.max(identified - extractionCompleted, 0);
    return {
      identified,
      newCount: Number(progress.delta ?? 0) || 0,
      extractionCompleted,
      extractionPending,
      rciGenerated,
      kgGenerated,
    };
  }

  private appendExtractedStories(batch: any[]): void {
    const mapped = this.mapStories(batch);
    const byId = new Map<string, UserStory>();
    for (const s of this.extractedStories) {
      if (s.storyId) byId.set(String(s.storyId), s);
    }
    for (const s of mapped) {
      const key = String(s.storyId || '');
      if (!key) continue;
      const existing = byId.get(key);
      byId.set(key, existing ? { ...existing, ...s } : s);
    }
    this.extractedStories = Array.from(byId.values());
  }

  private stopExtractionPolling(): void {
    this.extractPollSub?.unsubscribe();
    this.extractPollSub = null;
  }

  /**
   * After the BPD pipeline completes ("From other documents" flow):
   * - Extract versionId + test cases from the API response
   * - Push test cases directly into the NgRx store
   * - Build a synthetic TcgTestSuiteRow and navigate to testCaseDetail
   */
  private handleBpdTestCasesResult(result: TcgGenerateFormData): void {
    const raw = result?.testCases;

    // Extract versionId from common response shapes
    const versionId: string =
      raw?.data?.versionId ?? raw?.data?.id ??
      raw?.versionId ?? raw?.id ?? 'bpd-result';

    const llmConnectionId: string =
      raw?.data?.llmConnectionId ??
      raw?.data?.llmConnection ??
      raw?.llmConnectionId ??
      raw?.llmConnection ??
      '';

    if (llmConnectionId) {
      this.commonService.setLLMConnection(llmConnectionId);
    }

    // Extract test cases array from common response shapes
    let testCases: any[] = [];
    if (Array.isArray(raw?.data?.testCases)) {
      testCases = raw.data.testCases;
    } else if (Array.isArray(raw?.data)) {
      testCases = raw.data;
    } else if (Array.isArray(raw?.testCases)) {
      testCases = raw.testCases;
    } else if (Array.isArray(raw)) {
      testCases = raw;
    }

    // Accordion header binds to `testCase`; Test Steps binds to `testScenario`.
    // BPD generate API now returns title in testCase and numbered steps in testScenario.
    const mapped = testCases.map((tc: any, index: number) => {
      const storyId = tc.key || tc.userStoryId || tc.storyId || '';
      const displayId = tc.testCaseId ?? tc.id ?? '';
      return {
        ...tc,
        _id:         tc._id ?? `${storyId || 'story'}__${displayId || 'tc'}__${index}`,
        key:         storyId,
        userStoryId: storyId,
        testCaseId:  displayId,
        testCase:    tc.testCase || tc.title || tc.name || tc.summary || '',
        testScenario: tc.testScenario || '',
        testType:    this.formatGeneratedTestType(tc.testType ?? tc.testCaseType ?? tc.type),
        testCaseType: this.formatGeneratedTestType(tc.testType ?? tc.testCaseType ?? tc.type),
        subType:     tc.subType ?? tc.subtype ?? tc.testSubType ?? tc.testCaseSubType ?? '',
        priority:    tc.priority   ?? '',
      };
    });

    // Push into store so TcgTestCaseDetailComponent can read them
    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdSuccess({ testCases: mapped })
    );

    // Build a synthetic suite row so the detail component has the right context
    const suite: TcgTestSuiteRow = {
      id:             versionId,
      testSuiteName:  'Generated Test Cases',
      llmName:        '',
      timestamp:      new Date().toISOString(),
      testCasesCount: mapped.length,
      actionsLabel:   `${mapped.length} Test Cases`,
      llmConnectionId: llmConnectionId || undefined,
    };

    this.selectedSuite = suite;
    this.isBpdDetailView = true;
    this.activeView = 'testCaseDetail';
    this.syncHeaderActions();
    this.toaster.pop({ type: 'success', text: 'Test cases generated successfully.' });
  }

  onViewTestCases(suite: TcgTestSuiteRow): void {
    this.selectedSuite = suite;
    this.isBpdDetailView = false;
    this.activeView = "testCaseDetail";
    this.syncHeaderActions();
  }

  onGenerateTestCases(event: any): void {
    const rawCases = this.mapGeneratedTestCaseRows(event?.testCases ?? []);
    this.testCaseVersionId = event?.versionId ?? '';
    this.allGeneratedTestCaseRows = rawCases;
    this.previousTestCaseFilter = null;
    this.applyGeneratedTestCasesData(rawCases);
    this.activeView = 'testCases';
    this.syncHeaderActions();
    this.toaster.pop({ type: 'success', text: 'Test cases generated successfully.' });
  }

  onTestCasesAction(event: any): void {
    if (event?.type === 'add_to_suite') {
      this.openAddToSuiteModal(event.data ?? [], this.isImportDetailView);
      return;
    }
    if (event?.type === 'feedback_submit') {
      this.updateFromFeedback(event);
      return;
    }
    if (event?.type === 'filter') {
      this.openTestCaseFilter();
      return;
    }
    if (event?.type === 'open-user-story') {
      this.openUserStoryDetails(event.data);
      return;
    }
    if (event?.type === 'open-traceability') {
      this.openTraceabilityDetails(event.data);
    }
    if (event?.type === 'download') {
      this.onDownload(event);
    }

  }

  onDownload(event:any): void {
    const formattedData = event?.data?.map((row: any) => ({
      _id: row._id,
      key: row.userStoryId || '',
      storydesc: row.storydesc || '',
      testCaseId: row.testCaseId || '',
      testCase: row.testCase || '',
      prerequisites: row.prerequisites || '',
      testScenario: row.testScenario || '',
      expectedResult: row.expectedResult || '',
      storysummary:row?.storysummary || '',
      //priority:row?.priority || '',
      testCaseType:row?.testCaseType || row?.testType || '',
      subType:row?.subType || '',
    }));

    const payload = {
      testSuiteName: "TestSuiteDownload",
      selectedTestCaseCount: formattedData.length,
      testCaseReportDetails: formattedData,
    };
    this.restService.downloadExcel(this.projectId, payload).subscribe({
      next: (response: any) => {
        window.open(response?.data, "_blank");
      },
      error: (error: any) => {
        let errMessage = error?.message ? error?.message : MessageEnum.EXCEL_DOWNLOAD_FAILURE;
        this.toaster.pop({ type: 'error', text: errMessage});
      }
    });
  }

  private openTraceabilityDetails(data: { traceability?: string; key?: string }): void {
    if (!data?.traceability || !data?.key) {
      return;
    }

    const modalRef = this.modalService.open(TcgTraceabilityModalComponent, {
      centered: true,
      windowClass: 'user-story-modal',
      size: 'm',
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.title = 'Traceability';
  }

  /**
   * Same modal as generated-test-cases `openUserStory`:
   * TcgStoryDetailModalComponent with Description + Acceptance Criteria.
   */
  private openUserStoryDetails(row: any): void {
    if (!row) return;

    const acceptanceRaw =
      row.acceptanceCriteria ?? row.acceptancecriteria ?? row.acceptance_criteria;
    let acceptanceText = '';
    if (Array.isArray(acceptanceRaw)) {
      acceptanceText = acceptanceRaw
        .map((item: any) => String(item ?? '').trim())
        .filter(Boolean)
        .map((item: string) => `* ${item}`)
        .join('\n');
    } else if (acceptanceRaw != null) {
      acceptanceText = String(acceptanceRaw);
    }

    const data = {
      ...row,
      key: row.key ?? row.userStoryId ?? row.storyId ?? '',
      storysummary: row.storysummary ?? row.summary ?? row.title ?? '',
      storydesc: row.storydesc ?? row.description ?? '',
      acceptancecriteria: acceptanceText,
    };

    const modalRef = this.modalService.open(TcgStoryDetailModalComponent, {
      centered: true,
      windowClass: 'user-story-modal',
      size: 'xl',
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.contentList = userstoryDetails;
    modalRef.componentInstance.title = 'User Story Details';
  }

  /** Same PromptFilterModal (mode=tcg) used by user-stories filtering. */
  private openTestCaseFilter(): void {
    const storyIds = Array.from(
      new Set(
        this.allGeneratedTestCaseRows
          .map((tc: any) => String(tc.userStoryId ?? tc.key ?? tc.storyId ?? '').trim())
          .filter(Boolean)
      )
    );

    const ref = this.modalService.open(PromptFilterModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });
    ref.componentInstance.mode = 'tcg';
    ref.componentInstance.tcgUserStories = storyIds.map(id => ({ label: id, value: id }));
    ref.componentInstance.initialTcgFilter = this.previousTestCaseFilter;
    ref.componentInstance.onFilterReset = () => {
      this.previousTestCaseFilter = null;
      this.refreshGeneratedTestCaseRows(this.allGeneratedTestCaseRows);
    };
    ref.result.then(
      (result: TcgFilterResult) => this.applyTestCaseFilter(result),
      () => {}
    );
  }

  private applyTestCaseFilter(data: TcgFilterResult): void {
    if (data?.reset || (!data?.userStory && !data?.priority)) {
      this.previousTestCaseFilter = null;
      this.refreshGeneratedTestCaseRows(this.allGeneratedTestCaseRows);
      return;
    }
    this.previousTestCaseFilter = {
      userStory: data.userStory ?? '',
      priority: data.priority ?? '',
    };
    this.refreshGeneratedTestCaseRows(this.allGeneratedTestCaseRows);
  }

  private getFilteredTestCaseRows(rows: any[]): any[] {
    const filter = this.previousTestCaseFilter;
    if (!filter?.userStory && !filter?.priority) {
      return [...rows];
    }
    return rows.filter((tc: any) => {
      const storyId = String(tc.userStoryId ?? tc.key ?? tc.storyId ?? '');
      const priority = String(tc.priority ?? '');
      const storyMatch = !filter.userStory || storyId === filter.userStory;
      const priorityMatch =
        !filter.priority || priority.toUpperCase() === filter.priority.toUpperCase();
      return storyMatch && priorityMatch;
    });
  }

  /**
   * Same flow as tcg-test-case-detail `addFeedback` / generated-test-cases publish:
   * POST /tcg/testcase/update → GET by versionId → replace accordion rows with retrieved data.
   */
  private updateFromFeedback(event: any): void {
    if (this.isUpdatingFeedback) {
      return;
    }
    const feedback = (event?.feedback ?? '').toString().trim();
    const row = event?.data;
    const testcaseId = row?.testCaseId ?? row?.id ?? row?._id;
    if (!feedback || !testcaseId) {
      this.toaster.pop({ type: 'error', text: 'Please enter feedback before updating.' });
      return;
    }
    if (!this.testCaseVersionId) {
      this.toaster.pop({ type: 'error', text: 'Missing test case version. Please regenerate test cases.' });
      return;
    }

    const payload = [{
      projectId: this.projectId,
      testcaseId,
      userInput: feedback,
      versionId: this.testCaseVersionId,
    }];

    this.isUpdatingFeedback = true;
    this.restService.updateTestCase(payload).pipe(
      switchMap(() =>
        this.restService.getTestCaseByVersionId(this.projectId, this.testCaseVersionId)
      ),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        const refreshed = this.mapGeneratedTestCaseRows(res?.data?.testCases ?? []);
        this.allGeneratedTestCaseRows = refreshed;
        this.applyGeneratedTestCasesData(refreshed);
        this.isUpdatingFeedback = false;
        this.toaster.pop({ type: 'success', text: MessageEnum.FEEDBACK_SUBMIT_SUCCESS });
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isUpdatingFeedback = false;
        console.error('[TCG] Update from feedback failed:', err);
        this.toaster.pop({
          type: 'error',
          text: err?.error?.message || MessageEnum.FEEDBACK_SUBMIT_FAILURE,
        });
        this.cdr.detectChanges();
      },
    });
  }

  /** Display API test-type enums as the labels used in the accordion. */
  private formatGeneratedTestType(raw: string | null | undefined): string {
    const value = String(raw ?? '').trim();
    if (!value) return '';
    const normalized = value.toLowerCase().replace(/[_-]+/g, ' ');
    if (normalized.includes('regression')) return 'Regression';
    if (normalized.includes('sanity')) return 'Sanity';
    if (normalized.includes('smoke')) return 'Smoke';
    if (normalized.includes('non functional')) return 'Non-functional';
    return value;
  }

  /** Normalize API test cases for the ALM generated-test-cases accordion. */
  private mapGeneratedTestCaseRows(testCases: any[]): any[] {
    return (testCases ?? []).map((tc: any, index: number) => ({
      ...tc,
      userStoryId: tc.userStoryId ?? tc.key ?? tc.storyId ?? '',
      key: tc.key ?? tc.userStoryId ?? tc.storyId ?? '',
      testCaseType: this.formatGeneratedTestType(tc.testCaseType ?? tc.testType ?? tc.type),
      testType: this.formatGeneratedTestType(tc.testType ?? tc.testCaseType ?? tc.type),
      subType: tc.subType ?? tc.subtype ?? tc.testSubType ?? tc.testCaseSubType ?? '',
      priority: tc.priority ?? '',
      feedback: '',
      _id:
        tc?._id ??
        `${tc?.key || tc?.userStoryId || 'story'}__${tc?.testCaseId || 'tc'}__${index}`,
    }));
  }

  /** Replace accordion dataset so ngOnChanges picks up retrieved feedback updates. */
  private applyGeneratedTestCasesData(rows: any[]): void {
    const columns = this.generatedTestCases?.columns ?? [
      { mapping_key: 'userStoryId',     display_name: 'Story ID',          isVisible: true  },
      { mapping_key: 'testCaseId',      display_name: 'Test Case ID',      isVisible: true  },
      { mapping_key: 'testCaseType',    display_name: 'Test Type',         isVisible: true  },
      { mapping_key: 'priority',        display_name: 'Priority',          isVisible: true  },
      { mapping_key: 'testCase',        display_name: 'Test Case',         isVisible: true  },
      { mapping_key: 'testCaseType',    display_name: 'Test Type',         isVisible: false },
      { mapping_key: 'subType',         display_name: 'Sub Type',          isVisible: false },
      { mapping_key: 'traceability',    display_name: 'Traceability',      isVisible: false },
      { mapping_key: 'prerequisites',      display_name: 'Prerequisites',         isVisible: false },
      { mapping_key: 'testScenario',       display_name: 'Test Steps',            isVisible: false },
      { mapping_key: 'sanitizedTestSteps', display_name: 'Sanitized Test Steps',  isVisible: false },
      { mapping_key: 'expectedResult',     display_name: 'Expected Results',      isVisible: false },
    ];

    this.generatedTestCases = {
      columns: columns.map((c: any) => ({ ...c })),
      rows: this.getFilteredTestCaseRows(rows),
    };
  }

  private refreshGeneratedTestCaseRows(rows: any[]): void {
    if (!this.generatedTestCases) return;
    this.applyGeneratedTestCasesData(rows);
  }

  /** Opens the "Add to Test Suite" modal (new + existing suite). */
  private openAddToSuiteModal(selectedRows: any[], importFlow: boolean): void {
    if (!selectedRows?.length) {
      this.toaster.pop({ type: 'error', text: MessageEnum.DATA_SELECTION_NONE });
      return;
    }
    if (!importFlow && !this.testCaseVersionId) {
      this.toaster.pop({ type: 'error', text: 'Missing test case version. Please regenerate test cases.' });
      return;
    }

    const modalRef = this.modalService.open(ImportAddToSuiteModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'import-add-to-suite-modal',
      size: "lg",
    });
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.componentInstance.selectedTestCases = selectedRows;
    modalRef.componentInstance.persistMode = !importFlow;

    if (importFlow) {
      modalRef.componentInstance.connectionId = this.importConnectionId;
      modalRef.componentInstance.documentType = this.importDocumentType;
    }

    modalRef.result.then((result: any) => {
      if (!result) return;

      if (importFlow) {
        const count = selectedRows.length;
        this.toaster.pop({
          type: 'success',
          text: `${count} test case${count !== 1 ? 's' : ''} added to "${result.testSuiteName}" successfully.`,
        });
        this.onBackToList();
        return;
      }

      if (result.isNewSuite) {
        this.store.select(selectAllTestCaseVersions).pipe(take(1)).subscribe((suites) => {
          const name = (result.testSuiteName ?? '').trim().toLowerCase();
          const nameExists = (suites ?? []).some(
            (suite) => (suite.versionName ?? '').trim().toLowerCase() === name
          );
          if (nameExists) {
            this.toaster.pop({
              type: 'error',
              text: MessageEnum.TEST_SUITE_NAME_ALREADY_EXISTS,
            });
            return;
          }

          this.persistNewTestSuite(result.testSuiteName, selectedRows);
        });
        return;
      }

      this.persistNewTestSuite(result.testSuiteName, selectedRows, result.testSuiteId);
    }).catch(() => {
      // dismissed — no-op
    });
  }

  addImportedToTestSuite(selectedRows: any[]): void {
    this.openAddToSuiteModal(selectedRows, true);
  }

  private persistNewTestSuite(
    suiteName: string,
    selectedRows: any[],
    selectedVersionId: string | null = null
  ): void {
      const payload = {
        _id: this.testCaseVersionId,
        versionName: suiteName,
        selectedVersionId,
        isNewTestSuite: !selectedVersionId,
        llmConnectionId: null,
        testCases: selectedRows.map((tc: any) => ({
          testCaseId: tc.testCaseId,
          testCase: tc.testCase,
          testScenario: tc.testScenario ?? tc.testCase,
          expectedResult: tc.expectedResult ?? tc.expectedResults,
          prerequisites: tc.prerequisites,
          steps: tc.steps ?? tc.testSteps,
          _id: tc._id,
          key: tc.key ?? tc.userStoryId,
          storydesc: tc.storydesc ?? tc.description,
          storysummary: tc.storysummary ?? tc.summary,
          acceptancecriteria: tc?.acceptancecriteria,
          testCaseType: tc?.testCaseType,
          subType : tc?.subType,
          traceability : tc?.traceability,
          priority : tc?.priority
        })),
      };

      this.isPersistingSuite = true;
      this.restService.persistTestCase(this.projectId, payload).subscribe({
        next: () => {
          this.isPersistingSuite = false;
          this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SUITE_CREATION_SUCCESS });
          this.onBackToList();
        },
        error: (error: any) => {
          this.isPersistingSuite = false;
          console.error('[TCG] Persist test suite failed:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || MessageEnum.TEST_SUITE_CREATION_FAILURE,
          });
        },
      });
  }

  onBackToUserStories(): void {
    this.activeView = 'userStories';
    this.generatedTestCases = null;
    this.allGeneratedTestCaseRows = [];
    this.previousTestCaseFilter = null;
    this.testCaseVersionId = '';
    this.isUpdatingFeedback = false;
    this.syncHeaderActions();
  }

  onBackToList(): void {
    this.stopExtractionPolling();
    this.activeView         = "testSuites";
    this.selectedSuite      = null;
    this.isBpdDetailView    = false;
    this.isImportDetailView = false;
    this.importConnectionId = '';
    this.importDocumentType = 'xlsx';
    this.extractedStories = [];
    this.extractConfigId = '';
    this.extractFromTimestamp = null;
    this.processingCounts = null;
    this.isExtracting = false;
    this.generatedTestCases = null;
    this.allGeneratedTestCaseRows = [];
    this.previousTestCaseFilter = null;
    this.testCaseVersionId = '';
    this.isUpdatingFeedback = false;
    this.syncHeaderActions();
  }

  /** Show "+ Add Test Cases" dropdown only on the list view, aligned with the breadcrumb. */
  private syncHeaderActions(): void {
    if (this.activeView === "testSuites") {
      this.breadcrumbService.setPageActions([
        {
          key: "addTestCases",
          label: "+ Add Test Cases",
          variant: "primary",
          isDropdown: true,
          menuItems: [
            {
              key: TcgComponent.GENERATE_ACTION_KEY,
              label: "Generate Test Cases",
            },
            {
              key: TcgComponent.IMPORT_ACTION_KEY,
              label: "Import Test Cases",
            },
          ],
        },
      ]);
    } else {
      this.breadcrumbService.clearPageActions();
    }
  }

  /**
   * Opens the import modal from the "Add Missing Test Cases → Import Test Cases" dropdown
   * inside a suite's detail view. After field mapping + Import, extracted test cases are shown
   * in a preview popup where the user selects which ones to add to the current suite.
   */
  onImportTestCasesForSuite(
    context: { suiteId: string; suiteName: string; llmConnectionId: string | null },
    almConnectionId = ''
  ): void {
    const importRef = this.modalService.open(ImportTestCasesModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'import-test-cases-modal-window',
    });
    importRef.componentInstance.projectId = this.projectId;
    importRef.componentInstance.initialAlmConnectionId = almConnectionId;

    importRef.result.then((result: ImportTestCasesResult | OpenZephyrImportResult) => {
      if (this.isZephyrImportRequest(result)) {
        this.openZephyrImportModal(
          result.connectionId,
          result.connectionName,
          () => this.onImportTestCasesForSuite(context, result.connectionId),
          (imported) => this.openImportedSuitePreview(context, imported),
        );
        return;
      }
      this.openImportedSuitePreview(context, result);
      return;
    }).catch(() => { /* dismissed */ });
  }

  private openImportedSuitePreview(
    context: { suiteId: string; suiteName: string; llmConnectionId: string | null },
    result: ImportTestCasesResult,
  ): void {
    if (!result?.extractedTestCases?.length) return;

    const previewRef = this.modalService.open(ImportPreviewModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'import-preview-modal-window',
      size: "xl",
    });
    previewRef.componentInstance.extractedTestCases = result.extractedTestCases;
    previewRef.componentInstance.suiteId            = context.suiteId;
    previewRef.componentInstance.suiteName          = context.suiteName;
    previewRef.componentInstance.projectId          = this.projectId;
    previewRef.componentInstance.llmConnectionId    = context.llmConnectionId;
    previewRef.componentInstance.connectionId       = result.connectionId;
    previewRef.componentInstance.documentType       = result.documentType;

    previewRef.result.then((previewResult: ImportPreviewResult) => {
      if (previewResult) {
        this.toaster.pop({
          type: 'success',
          text: `${previewResult.addedCount} test case${previewResult.addedCount !== 1 ? 's' : ''} added to "${previewResult.suiteName}" successfully.`,
        });
        this.store.dispatch(
          TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
            projectId: this.projectId,
            versionId: context.suiteId,
          })
        );
      }
    }).catch(() => { /* dismissed */ });
  }

  onImportTestCases(almConnectionId = ''): void {
    const ref = this.modalService.open(ImportTestCasesModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'import-test-cases-modal-window',
    });
    ref.componentInstance.projectId = this.projectId;
    ref.componentInstance.initialAlmConnectionId = almConnectionId;

    ref.result.then((result: ImportTestCasesResult | OpenZephyrImportResult) => {
      if (this.isZephyrImportRequest(result)) {
        this.openZephyrImportModal(
          result.connectionId,
          result.connectionName,
          () => this.onImportTestCases(result.connectionId),
          (imported) => this.handleImportTestCasesResult(imported),
        );
        return;
      }
      if (result) {
        this.handleImportTestCasesResult(result);
      }
    }).catch(() => {
      // dismissed — no-op
    });
  }

  private openZephyrImportModal(
    connectionId: string,
    connectionName: string,
    onBack: () => void,
    onImported: (result: ImportTestCasesResult) => void,
  ): void {
    const ref = this.modalService.open(ImportZephyrModalComponent, {
      centered: true,
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'import-zephyr-modal-window',
    });
    ref.componentInstance.projectId = this.projectId;
    ref.componentInstance.connectionId = connectionId;
    ref.componentInstance.connectionName = connectionName;

    ref.result.then((action: ZephyrModalResult) => {
      if (action === 'back') {
        onBack();
        return;
      }
      if (action?.imported) {
        onImported({
          source: 'zephyr',
          documentType: 'zephyr',
          connectionId: action.connectionId,
          connectionName: action.connectionName,
          fieldMappings: action.fieldMappings,
          extractedTestCases: action.extractedTestCases,
        });
      }
    }).catch(() => { /* dismissed */ });
  }

  private isZephyrImportRequest(
    result: ImportTestCasesResult | OpenZephyrImportResult | undefined
  ): result is OpenZephyrImportResult {
    return !!result && 'openZephyr' in result && result.openZephyr === true;
  }

  /**
   * Maps extracted test cases from the import flow and navigates to the
   * testCaseDetail view — mirrors handleBpdTestCasesResult() for consistency.
   */
  private handleImportTestCasesResult(result: ImportTestCasesResult): void {
    const rawCases: any[] = result.extractedTestCases ?? [];

    const mapped = rawCases.map((tc: any, index: number) => {
      const storyId  = tc.key ?? tc.userStoryId ?? tc.storyId ?? '';
      const displayId = tc.testCaseId ?? tc.id ?? '';
      return {
        ...tc,
        _id:          tc._id ?? `${storyId || 'import'}__${displayId || 'tc'}__${index}`,
        key:          storyId,
        userStoryId:  storyId,
        testCaseId:   displayId,
        testCase:     tc.testCase || tc.title || tc.name || tc.summary || '',
        testScenario: tc.testScenario ?? '',
        testType:     this.formatGeneratedTestType(tc.testType ?? tc.testCaseType ?? tc.type),
        testCaseType: this.formatGeneratedTestType(tc.testType ?? tc.testCaseType ?? tc.type),
        subType:      tc.subType ?? tc.subtype ?? tc.testSubType ?? tc.testCaseSubType ?? '',
        priority:     tc.priority ?? '',
      };
    });

    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdSuccess({ testCases: mapped })
    );

    const suite: TcgTestSuiteRow = {
      id:             result.connectionId,
      testSuiteName:  `Imported from ${result.connectionName}`,
      llmName:        '',
      timestamp:      new Date().toISOString(),
      testCasesCount: mapped.length,
      actionsLabel:   `${mapped.length} Test Case${mapped.length !== 1 ? 's' : ''}`,
    };

    this.selectedSuite       = suite;
    this.isBpdDetailView     = true;
    this.isImportDetailView  = true;
    this.importConnectionId  = result.connectionId;
    this.importDocumentType  = result.documentType;
    this.activeView          = 'testCaseDetail';
    this.syncHeaderActions();

    const count  = mapped.length;
    const plural = count !== 1 ? 's' : '';
    const text   = count > 0
      ? `${count} test case${plural} imported from "${result.connectionName}" successfully.`
      : `Import from "${result.connectionName}" completed.`;
    this.toaster.pop({ type: 'success', text });
  }
}
